import React, { useEffect, useRef, useState } from 'react';

/**
 * Cyberpunk 3D binary-rain splash screen.
 *
 * Two layers:
 *   (a) Vertical column rain — 0/1 digits falling from the top, classic Matrix
 *       but limited to binary so it looks like data.
 *   (b) 3D particle field — digits scattered in 3D space (x,y,z) that fly toward
 *       the camera, projected with perspective. Far particles are small + dim,
 *       near particles are large + bright. Recycle once they cross the camera.
 *
 * A central additive radial glow simulates the data "horizon" vanishing point.
 *
 * No text labels, no logo, no watermark — pure procedural canvas animation.
 */
export default function SplashScreen({ onDone, durationMs = 2800 }) {
  const canvasRef = useRef(null);
  const [exit, setExit] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0;
    let raf;

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // === Vertical column rain (foreground) ===
    const colWidth = 18;
    const cols = Math.ceil(W / colWidth);
    const drops = Array(cols).fill(0).map(() => ({
      y: Math.random() * H,
      speed: 1.5 + Math.random() * 3,
      lastChar: Math.random() < 0.5 ? '0' : '1',
    }));

    // === 3D particle cloud ===
    const FOCAL = 360;
    const Z_FAR = 1200;
    const Z_NEAR = 6;
    const PARTICLES = 220;
    const particles = Array(PARTICLES).fill(0).map(() => ({
      x: (Math.random() - 0.5) * 1400,
      y: (Math.random() - 0.5) * 900,
      z: Z_NEAR + Math.random() * (Z_FAR - Z_NEAR),
      ch: Math.random() < 0.5 ? '0' : '1',
      speed: 4 + Math.random() * 6,
    }));

    const recycle = (p) => {
      p.x = (Math.random() - 0.5) * 1400;
      p.y = (Math.random() - 0.5) * 900;
      p.z = Z_FAR;
      p.ch = Math.random() < 0.5 ? '0' : '1';
      p.speed = 4 + Math.random() * 6;
    };

    const cx = () => W / 2;
    const cy = () => H / 2;

    let last = 0;
    const tick = (t) => {
      const dt = Math.min(40, t - last) || 16;
      last = t;

      // Fading trail
      ctx.fillStyle = 'rgba(2, 6, 23, 0.22)';
      ctx.fillRect(0, 0, W, H);

      // Central radial glow (additive)
      const grad = ctx.createRadialGradient(cx(), cy(), 0, cx(), cy(), Math.max(W, H) * 0.45);
      grad.addColorStop(0, 'rgba(120, 255, 180, 0.18)');
      grad.addColorStop(0.4, 'rgba(57, 255, 20, 0.06)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // (a) Vertical column rain
      ctx.font = '14px "JetBrains Mono", monospace';
      ctx.textAlign = 'center';
      for (let i = 0; i < cols; i++) {
        const d = drops[i];
        d.y += d.speed * (dt / 16);
        if (d.y > H + 20) {
          d.y = -Math.random() * 200;
          d.speed = 1.5 + Math.random() * 3;
        }
        const ch = Math.random() < 0.08 ? (Math.random() < 0.5 ? '0' : '1') : d.lastChar;
        d.lastChar = ch;
        const x = i * colWidth + colWidth / 2;
        // Lead character — bright white-green
        ctx.fillStyle = 'rgba(220, 255, 220, 0.95)';
        ctx.fillText(ch, x, d.y);
        // Trail
        for (let k = 1; k < 10; k++) {
          const trailCh = Math.random() < 0.5 ? '0' : '1';
          const alpha = (1 - k / 10) * 0.55;
          ctx.fillStyle = `rgba(57, 255, 20, ${alpha})`;
          ctx.fillText(trailCh, x, d.y - k * 16);
        }
      }

      // (b) 3D particle field
      ctx.textAlign = 'center';
      // Sort back-to-front for proper layering
      particles.sort((a, b) => b.z - a.z);
      for (const p of particles) {
        p.z -= p.speed * (dt / 16);
        if (p.z <= Z_NEAR) { recycle(p); continue; }
        const scale = FOCAL / p.z;
        const sx = cx() + p.x * scale;
        const sy = cy() + p.y * scale;
        if (sx < -40 || sx > W + 40 || sy < -40 || sy > H + 40) continue;
        const size = Math.max(6, 28 * scale);
        const alpha = Math.min(1, 0.15 + (1 - p.z / Z_FAR) * 1.2);
        // Bright near-camera particles get a glow
        if (scale > 0.6) {
          ctx.shadowColor = 'rgba(57, 255, 20, 0.9)';
          ctx.shadowBlur = 14 * scale;
        } else {
          ctx.shadowBlur = 0;
        }
        const greenStrength = 120 + Math.floor(135 * (1 - p.z / Z_FAR));
        ctx.fillStyle = scale > 0.9
          ? `rgba(235, 255, 220, ${alpha})`
          : `rgba(${Math.floor(60 * scale)}, ${greenStrength}, ${Math.floor(60 * scale)}, ${alpha})`;
        ctx.font = `bold ${size}px "JetBrains Mono", monospace`;
        ctx.fillText(p.ch, sx, sy);
      }
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const exitTimer = setTimeout(() => setExit(true), durationMs - 500);
    const doneTimer = setTimeout(() => onDone && onDone(), durationMs);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [durationMs, onDone]);

  return (
    <div
      className={`fixed inset-0 z-[100] bg-black transition-opacity duration-500 ${exit ? 'opacity-0' : 'opacity-100'}`}
      data-testid="matrix-splash"
    >
      <canvas ref={canvasRef} className="absolute inset-0 block" />
    </div>
  );
}
